import { useEffect, useRef, useState } from "react";
import AdminSidebar from "../components/sidebar/admin_sidebar";
import PageTitle from "../../../components/page_title/page_title";
import axiosInstance from "../../../AxiosInstance";
import JSONViewer from "../../../components/json_viewer/JSONViewer";
import { PrimaryButton, SecondaryButton } from "../../../components/input/inputs";
import Modal from "../../../components/modal/modal";
import Toast from "../../../components/toast/Toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faBroom, faEdit, faSave, faTimes, faBolt, faPlus, faPencil, faToggleOn, faSlidersH, faFont } from "@fortawesome/free-solid-svg-icons";
import { PlaceholderInput, PlaceholderTextarea } from "../../../components/input/PlaceholderTextarea";
import styles from "./mqtt_config.module.css";
import sidebarStyles from "../components/sidebar/admin_sidebar.module.css";
import { useTranslation } from 'react-i18next';

// Multi-pass normalization: replaces {placeholder} patterns for JSON validation
function normalizePlaceholders(text) {
    let result = text;
    let prev;
    do {
        prev = result;
        result = result
            .replace(/"[^"]*"/g, m => m.replace(/\{[^{}]*\}/g, 'x'))
            .replace(/\{[^{}]*\}/g, '"x"');
    } while (result !== prev);
    return result;
}

function tryParseJson(text) {
    if (!text || !text.trim()) return null;
    try {
        JSON.parse(normalizePlaceholders(text));
        return null;
    } catch (e) {
        return e.message;
    }
}

function JsonTextarea({label, value, onChange, placeholder, placeholders}) {
    const { t } = useTranslation();
    const error = tryParseJson(value);

    const formatJson = () => {
        if (!value || !value.trim()) return;
        const tokens = {};
        let i = 0;
        let tokenized = value;
        let prev;
        do {
            prev = tokenized;
            tokenized = tokenized.replace(/"[^"]*\{[^{}]*\}[^"]*"/g, (match) => {
                const k = `__PH${i++}__`;
                tokens[`"${k}"`] = match;
                return `"${k}"`;
            });
            tokenized = tokenized.replace(/\{[^"{}][^{}]*\}/g, m => {
                const k = `"__PH${i++}__"`;
                tokens[k] = m;
                return k;
            });
        } while (tokenized !== prev);
        try {
            const pretty = JSON.stringify(JSON.parse(tokenized), null, 2);
            let restored = pretty;
            let rPrev;
            do {
                rPrev = restored;
                restored = restored.replace(/"__PH\d+__"/g, m => tokens[m] || m);
            } while (restored !== rPrev);
            onChange(restored);
        } catch (_) {}
    };

    const labelStyle = {fontSize: '12px', fontWeight: '600', color: '#64748B', marginBottom: '4px', display: 'block'};
    const inputStyle = {width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', marginBottom: '10px', boxSizing: 'border-box'};

    return (
        <div style={{marginTop: '12px', marginBottom: '10px'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px'}}>
                <span style={labelStyle}>{label}</span>
                <button
                    type="button" onClick={formatJson}
                    disabled={!value || !!error}
                    style={{
                        fontSize: '11px', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer',
                        border: '1px solid #C7D2FE', background: '#EEF2FF', color: '#4F46E5',
                        fontWeight: 600, opacity: (!value || !!error) ? 0.4 : 1,
                    }}
                >
                    {t('variables.formatJson')}
                </button>
            </div>
            <PlaceholderTextarea
                value={value || ''}
                onChange={onChange}
                placeholder={placeholder}
                placeholders={placeholders}
                style={{
                    ...inputStyle,
                    fontFamily: 'monospace', fontSize: '12px',
                    minHeight: '80px', resize: 'vertical', marginBottom: error ? '2px' : '0',
                    border: `1px solid ${error ? '#EF4444' : '#E2E8F0'}`,
                    backgroundColor: error ? '#FFF8F8' : '#fff',
                }}
            />
            {error && (
                <div style={{fontSize: '11px', color: '#EF4444', marginBottom: '6px'}}>
                    {t('variables.invalidJson', {error})}
                </div>
            )}
        </div>
    );
}

export default function MQTTConfig() {
    const { t } = useTranslation();
    const [deviceType, setDeviceType] = useState(null);
    const [mqttTopic, setMqttTopic] = useState('');
    const [examplePayload, setExamplePayload] = useState('');
    const [parsedJSON, setParsedJSON] = useState(null);
    const [jsonError, setJsonError] = useState('');
    const [selectedPaths, setSelectedPaths] = useState([]);
    const [variables, setVariables] = useState([]);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [topicDescription, setTopicDescription] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
    const [editingVariableId, setEditingVariableId] = useState(null);
    const [deviceIdSegmentIndex, setDeviceIdSegmentIndex] = useState(null);

    // GPS Configuration
    const [hasGPS, setHasGPS] = useState(false);
    const [latitudePath, setLatitudePath] = useState('');
    const [longitudePath, setLongitudePath] = useState('');
    const [sosPath, setSosPath] = useState('');

    // Indoor Positioning Configuration
    const [beaconListPath, setBeaconListPath] = useState('');
    const [beaconMacKey, setBeaconMacKey] = useState('mac');
    const [beaconRssiKey, setBeaconRssiKey] = useState('rssi');

    // Battery Configuration
    const [hasBattery, setHasBattery] = useState(false);
    const [batteryPath, setBatteryPath] = useState('');
    const [batteryIsPercentage, setBatteryIsPercentage] = useState(true);
    const [batteryMinVoltage, setBatteryMinVoltage] = useState('');
    const [batteryMaxVoltage, setBatteryMaxVoltage] = useState('');

    // Predictive Maintenance Configuration
    const [isPredictiveMaintenance, setIsPredictiveMaintenance] = useState(false);
    const [predictiveMaintenanceSystems, setPredictiveMaintenanceSystems] = useState([]);
    const [selectedPMSystemId, setSelectedPMSystemId] = useState(null);

    // LoRa / Decoder Configuration
    const [isEncrypted, setIsEncrypted] = useState(false);
    const [loraPayloadPath, setLoraPayloadPath] = useState('');
    const [loraFportPath, setLoraFportPath] = useState('');
    const [loraDecoder, setLoraDecoder] = useState('');

    // LoRa Decoder Test
    const [testPayloadB64, setTestPayloadB64] = useState('');
    const [testFport, setTestFport] = useState('1');
    const [testDecoderError, setTestDecoderError] = useState('');
    const [testDecoderSuccess, setTestDecoderSuccess] = useState(false);
    const [decoderResultData, setDecoderResultData] = useState(null);

    // Modbus RTU / RAK7431 Configuration
    const [isModbus, setIsModbus] = useState(false);
    const [modbusTransparent, setModbusTransparent] = useState(false);

    // Name editing
    const [isEditingName, setIsEditingName] = useState(false);
    const [deviceTypeName, setDeviceTypeName] = useState('');

    // Action Button Templates
    const EMPTY_BTN_TEMPLATE = {name: '', button_type: 'button', action_type: '5', mqtt_connection: '', mqtt_topic: '', mqtt_payload: '', api_connection: '', api_endpoint: '', api_body: '', boolean_format: 'raw', slider_min: '0', slider_max: '', linked_variable: '', timeout_seconds: '15'};
    const [btnTemplates, setBtnTemplates] = useState([]);
    const [showBtnModal, setShowBtnModal] = useState(false);
    const [editingBtnId, setEditingBtnId] = useState(null);
    const [btnForm, setBtnForm] = useState(EMPTY_BTN_TEMPLATE);
    const [btnMqttConnections, setBtnMqttConnections] = useState([]);
    const [btnApiConnections, setBtnApiConnections] = useState([]);
    const [btnFormError, setBtnFormError] = useState('');

    const hasLoadedExampleRef = useRef(false);

    useEffect(() => {
        const stored = localStorage.getItem('currentDeviceType');
        if (stored) {
            const dt = JSON.parse(stored);
            setDeviceType(dt);
            setDeviceTypeName(dt.name || '');
            loadVariables(dt.id);
            loadBtnTemplates(dt.id);
        } else {
            window.location.href = '/admin/device_types';
        }
        // Load available predictive maintenance systems
        loadPredictiveMaintenanceSystems();
    }, []);

    const loadPredictiveMaintenanceSystems = async () => {
        try {
            const response = await axiosInstance.get('predictive-maintenance-systems/');
            setPredictiveMaintenanceSystems(response.data || []);
        } catch (err) {
            console.error('Error loading predictive maintenance systems:', err);
        }
    };

    const loadBtnTemplates = (deviceTypeId) => {
        const toArr = d => Array.isArray(d) ? d : (d?.results || []);
        axiosInstance.get(`device_type_action_button/?device_type_id=${deviceTypeId}`).then(res => setBtnTemplates(toArr(res.data)));
        // Also load connections for the first project found (global connections or best effort)
        axiosInstance.get('mqtt_connection/').then(res => setBtnMqttConnections(toArr(res.data))).catch(() => {});
        axiosInstance.get('api_connection/').then(res => setBtnApiConnections(toArr(res.data))).catch(() => {});
    };

    const openNewBtnTemplate = () => {
        setEditingBtnId(null);
        setBtnForm(EMPTY_BTN_TEMPLATE);
        setShowBtnModal(true);
    };

    const openEditBtnTemplate = (tmpl) => {
        setEditingBtnId(tmpl.id);
        setBtnForm({
            name: tmpl.name || '',
            button_type: tmpl.button_type || 'button',
            action_type: String(tmpl.action_type),
            mqtt_connection: tmpl.mqtt_connection ? String(tmpl.mqtt_connection) : '',
            mqtt_topic: tmpl.mqtt_topic || '',
            mqtt_payload: tmpl.mqtt_payload || '',
            api_connection: tmpl.api_connection ? String(tmpl.api_connection) : '',
            api_endpoint: tmpl.api_endpoint || '',
            api_body: tmpl.api_body || '',
            boolean_format: tmpl.boolean_format || 'raw',
            slider_min: tmpl.slider_min !== null && tmpl.slider_min !== undefined ? String(tmpl.slider_min) : '0',
            slider_max: tmpl.slider_max !== null && tmpl.slider_max !== undefined ? String(tmpl.slider_max) : '',
            linked_variable: tmpl.linked_variable ? String(tmpl.linked_variable) : '',
            timeout_seconds: tmpl.timeout_seconds !== undefined ? String(tmpl.timeout_seconds) : '15',
        });
        setShowBtnModal(true);
    };

    const closeBtnModal = () => {
        setShowBtnModal(false);
        setEditingBtnId(null);
        setBtnForm(EMPTY_BTN_TEMPLATE);
        setBtnFormError('');
    };

    const saveBtnTemplate = () => {
        if (!deviceType) return;
        if (!btnForm.name.trim()) {
            setBtnFormError(t('adminDeviceTypes.nameField') + ' ' + t('common.isRequired', 'es obligatorio'));
            return;
        }
        setBtnFormError('');
        const payload = {
            ...btnForm,
            device_type: deviceType.id,
            action_type: parseInt(btnForm.action_type),
            mqtt_connection: btnForm.mqtt_connection || null,
            api_connection: btnForm.api_connection || null,
            slider_min: parseFloat(btnForm.slider_min) || 0,
            slider_max: btnForm.slider_max !== '' && btnForm.slider_max !== null ? parseFloat(btnForm.slider_max) : null,
            linked_variable: btnForm.linked_variable || null,
            timeout_seconds: parseInt(btnForm.timeout_seconds) || 15,
        };
        if (editingBtnId) {
            axiosInstance.patch(`device_type_action_button/${editingBtnId}/`, payload).then(res => {
                setBtnTemplates(prev => prev.map(t => t.id === editingBtnId ? res.data : t));
                closeBtnModal();
            });
        } else {
            axiosInstance.post('device_type_action_button/', payload).then(res => {
                setBtnTemplates(prev => [...prev, res.data]);
                closeBtnModal();
            });
        }
    };

    const deleteBtnTemplate = (id) => {
        axiosInstance.delete(`device_type_action_button/${id}/`).then(() => {
            setBtnTemplates(prev => prev.filter(t => t.id !== id));
        });
    };

    const loadVariables = (deviceTypeId) => {
        axiosInstance.get(`device_type/${deviceTypeId}/`).then((res) => {
            setVariables(res.data.variables || []);

            // Load device_id_segment configuration if exists
            if (res.data.device_id_segment !== undefined && res.data.device_id_segment !== null) {
                setDeviceIdSegmentIndex(res.data.device_id_segment);
            }

            // Load GPS configuration
            setHasGPS(res.data.has_gps || false);
            setLatitudePath(res.data.latitude_path || '');
            setLongitudePath(res.data.longitude_path || '');
            setSosPath(res.data.sos_path || '');

            // Load Indoor Positioning configuration
            setBeaconListPath(res.data.beacon_list_path || '');
            setBeaconMacKey(res.data.beacon_mac_key || 'mac');
            setBeaconRssiKey(res.data.beacon_rssi_key || 'rssi');

            // Load Battery configuration
            setHasBattery(res.data.has_battery || false);
            setBatteryPath(res.data.battery_path || '');
            setBatteryIsPercentage(res.data.battery_is_percentage !== false); // default true
            setBatteryMinVoltage(res.data.battery_min_voltage || '');
            setBatteryMaxVoltage(res.data.battery_max_voltage || '');

            // Load Predictive Maintenance configuration
            setIsPredictiveMaintenance(res.data.is_predictive_maintenance || false);
            setSelectedPMSystemId(res.data.predictive_maintenance_system?.id || res.data.predictive_maintenance_system || null);

            // Load LoRa configuration
            setIsEncrypted(res.data.is_encrypted || false);
            setLoraPayloadPath(res.data.lora_payload_path || '');
            setLoraFportPath(res.data.lora_fport_path || '');
            setLoraDecoder(res.data.lora_decoder || '');

            // Load Modbus configuration
            setIsModbus(res.data.is_modbus || false);
            setModbusTransparent(res.data.modbus_transparent || false);

            // Load example payload only on first load
            if (!hasLoadedExampleRef.current && res.data.example_payload) {
                handlePayloadChange(res.data.example_payload);
                hasLoadedExampleRef.current = true;
            }

            // For decoder devices, restore last decoder test result from DB
            if (res.data.is_encrypted && res.data.decoder_last_result) {
                try { setDecoderResultData(JSON.parse(res.data.decoder_last_result)); } catch (e) {}
            }

            // Load MQTT topic - priority: DeviceType topic (for predictive maintenance) > first variable topic
            if (res.data.mqtt_topic) {
                // If DeviceType has a topic (predictive maintenance), use it
                setMqttTopic(res.data.mqtt_topic);
                setTopicDescription(res.data.mqtt_topic_description || '');
            } else if (res.data.variables && res.data.variables.length > 0) {
                // Otherwise, load from first variable to show segments
                const firstVariable = res.data.variables[0];
                if (firstVariable.mqtt_topic) {
                    setMqttTopic(firstVariable.mqtt_topic);
                    setTopicDescription(firstVariable.mqtt_topic_description || '');
                }
            }
        }).catch(err => {
            console.error('Error loading variables:', err);
        });
    };

    const handlePayloadChange = (value) => {
        setExamplePayload(value);
        setJsonError('');

        if (!value.trim()) {
            setParsedJSON(null);
            return;
        }

        try {
            const parsed = JSON.parse(value);
            setParsedJSON(parsed);
        } catch (e) {
            setJsonError('Invalid JSON: ' + e.message);
            setParsedJSON(null);
        }
    };

    const handlePropertySelect = (path, value, type) => {
        setSelectedPaths(prev => {
            if (prev.some(p => p.path === path)) {
                // Deseleccionar
                return prev.filter(p => p.path !== path);
            } else {
                // Seleccionar
                return [...prev, { path, value, type }];
            }
        });
    };

    const removeSelectedPath = (pathToRemove) => {
        setSelectedPaths(prev => prev.filter(p => p.path !== pathToRemove));
    };

    const saveConfiguration = () => {
        if (!mqttTopic.trim()) {
            setToast({ show: true, message: t('adminDeviceTypes.mqttTopicRequired'), type: 'error' });
            return;
        }

        if (selectedPaths.length === 0) {
            setToast({ show: true, message: t('adminDeviceTypes.selectVariableFirst'), type: 'error' });
            return;
        }

        setShowSaveModal(true);
    };

    const confirmSave = async () => {
        try {
            // Validate that deviceType exists
            if (!deviceType || !deviceType.id) {
                setToast({
                    show: true,
                    message: t('adminDeviceTypes.deviceTypeNotFound'),
                    type: 'error'
                });
                return;
            }

            // Create variables in the backend
            // Note: device_id_segment is already saved automatically when clicking on the segment
            const promises = selectedPaths.map(selected => {
                const variableName = selected.customName || selected.path.split('.').pop();

                const payload = {
                    device_type_id: deviceType.id,
                    name: variableName,
                    json_path: selected.path,
                    unit: selected.unit || '',
                    data_type: selected.overrideType || selected.type,
                    mqtt_topic: mqttTopic,
                    mqtt_topic_description: topicDescription,
                    graphic_type: 0 // Line por defecto
                };

                console.log('Creating variable with payload:', payload);

                return axiosInstance.post('device_variable/', payload);
            });

            await Promise.all(promises);

            // Reload variables
            loadVariables(deviceType.id);

            // Only clear selections, keep topic and JSON
            setSelectedPaths([]);
            setShowSaveModal(false);

            setToast({
                show: true,
                message: `${selectedPaths.length} variable(s) created successfully`,
                type: 'success'
            });
        } catch (error) {
            console.error('Error saving variables:', error);
            console.error('Error response:', error.response?.data);

            // Show more detailed error message
            let errorMessage = 'Error saving variables';
            if (error.response?.data) {
                // If the backend returns error details, show them
                const errorData = error.response.data;
                if (typeof errorData === 'object') {
                    errorMessage += ': ' + JSON.stringify(errorData);
                } else {
                    errorMessage += ': ' + errorData;
                }
            }

            setToast({
                show: true,
                message: errorMessage,
                type: 'error',
                duration: 10000 // Show longer so it can be read
            });
        }
    };

    const updateSelectedPathMetadata = (path, field, value) => {
        setSelectedPaths(prev => prev.map(p =>
            p.path === path ? { ...p, [field]: value } : p
        ));
    };

    const deleteVariable = (variableId) => {
        setConfirmModal({
            show: true,
            title: 'Delete Variable',
            message: 'Are you sure you want to delete this variable?',
            onConfirm: () => {
                setConfirmModal({ show: false });
                axiosInstance.delete(`device_variable/${variableId}/`).then(() => {
                    loadVariables(deviceType.id);
                    setToast({ show: true, message: t('adminDeviceTypes.variableDeleted'), type: 'success' });
                    if (editingVariableId === variableId) {
                        clearForm();
                        setEditingVariableId(null);
                    }
                }).catch(err => {
                    console.error('Error deleting variable:', err);
                    setToast({ show: true, message: t('adminDeviceTypes.errorDeletingVariable'), type: 'error' });
                });
            }
        });
    };

    const getValueFromPath = (obj, path) => {
        if (!obj || !path) return null;
        return path.split('.').reduce((curr, key) => curr?.[key], obj);
    };

    const loadVariableForEdit = (variable) => {
        // Load variable data into the form
        setMqttTopic(variable.mqtt_topic || '');
        setTopicDescription(variable.mqtt_topic_description || '');

        // Keep the existing example payload (do not clear it)
        // Extract the example value for this variable's json_path from the active JSON source
        const activeJSON = decoderResultData || parsedJSON;
        const exampleValue = activeJSON ? getValueFromPath(activeJSON, variable.json_path) : null;

        // Create a selected path with the variable data
        setSelectedPaths([{
            path: variable.json_path,
            value: exampleValue,
            type: variable.data_type || 'number',
            customName: variable.name,
            unit: variable.unit || ''
        }]);

        setEditingVariableId(variable.id);

        // Scroll up to see the form
        window.scrollTo({ top: 0, behavior: 'smooth' });

        setToast({
            show: true,
            message: `Editing: ${variable.name}. You can paste a JSON to change the path.`,
            type: 'info',
            duration: 5000
        });
    };

    const updateVariable = async () => {
        if (!editingVariableId || selectedPaths.length === 0) {
            return;
        }

        // Validate that deviceType exists
        if (!deviceType || !deviceType.id) {
            setToast({
                show: true,
                message: t('adminDeviceTypes.deviceTypeNotFound'),
                type: 'error'
            });
            return;
        }

        try {
            const selected = selectedPaths[0];
            const variableName = selected.customName || selected.path.split('.').pop();

            const payload = {
                device_type_id: deviceType.id,
                name: variableName,
                json_path: selected.path,
                unit: selected.unit || '',
                data_type: selected.overrideType || selected.type,
                mqtt_topic: mqttTopic,
                mqtt_topic_description: topicDescription,
                graphic_type: 0
            };

            console.log('Updating variable with payload:', payload);

            await axiosInstance.put(`device_variable/${editingVariableId}/`, payload);

            // Reload variables
            loadVariables(deviceType.id);

            // Clear form
            clearForm();
            setEditingVariableId(null);

            setToast({
                show: true,
                message: t('adminDeviceTypes.variableUpdated'),
                type: 'success'
            });
        } catch (error) {
            console.error('Error updating variable:', error);
            console.error('Error response:', error.response?.data);

            // Show more detailed error message
            let errorMessage = 'Error updating variable';
            if (error.response?.data) {
                const errorData = error.response.data;
                if (typeof errorData === 'object') {
                    errorMessage += ': ' + JSON.stringify(errorData);
                } else {
                    errorMessage += ': ' + errorData;
                }
            }

            setToast({
                show: true,
                message: errorMessage,
                type: 'error',
                duration: 10000
            });
        }
    };

    const cancelEdit = () => {
        clearForm();
        setEditingVariableId(null);
        setToast({ show: true, message: t('adminDeviceTypes.editCancelled'), type: 'info' });
    };

    const updateAllVariableTopics = async () => {
        if (!mqttTopic.trim()) {
            setToast({ show: true, message: t('adminDeviceTypes.enterTopicFirst'), type: 'error' });
            return;
        }
        setConfirmModal({
            show: true,
            title: 'Update topic for all variables',
            message: `Update the MQTT topic for all ${variables.length} variable(s) to: ${mqttTopic}`,
            onConfirm: async () => {
                setConfirmModal({ show: false });
                try {
                    await axiosInstance.post(`device_type/${deviceType.id}/update_all_topics/`, { mqtt_topic: mqttTopic });
                    setToast({ show: true, message: t('adminDeviceTypes.topicUpdated'), type: 'success' });
                    loadVariables(deviceType.id);
                } catch (error) {
                    setToast({ show: true, message: 'Error updating topics: ' + (error.response?.data?.error || error.message), type: 'error' });
                }
            }
        });
    };

    const clearForm = () => {
        setExamplePayload('');
        setParsedJSON(null);
        setSelectedPaths([]);
        setJsonError('');
    };

    const handleTopicSegmentClick = async (index) => {
        const newIndex = deviceIdSegmentIndex === index ? null : index;

        try {
            // Save immediately to the backend
            await axiosInstance.patch(`device_type/${deviceType.id}/`, {
                device_id_segment: newIndex
            });

            // Update local state
            setDeviceIdSegmentIndex(newIndex);

            // Show success message
            setToast({
                show: true,
                message: newIndex !== null
                    ? `Segment ${newIndex} saved as Device ID position`
                    : 'Device ID position removed',
                type: 'success'
            });
        } catch (error) {
            console.error('Error saving device_id_segment:', error);
            setToast({
                show: true,
                message: 'Error saving Device ID position: ' + (error.response?.data?.message || error.message),
                type: 'error'
            });
        }
    };

    const saveDeviceTypeConfig = async (config) => {
        try {
            await axiosInstance.patch(`device_type/${deviceType.id}/`, config);
            return true;
        } catch (error) {
            console.error('Error saving config:', error);
            setToast({
                show: true,
                message: 'Error saving: ' + (error.response?.data?.message || error.message),
                type: 'error'
            });
            return false;
        }
    };

    const handleNameChange = async () => {
        if (!deviceTypeName.trim()) {
            setToast({
                show: true,
                message: 'Name cannot be empty',
                type: 'error'
            });
            return;
        }

        const success = await saveDeviceTypeConfig({ name: deviceTypeName });
        if (success) {
            setIsEditingName(false);
            // Update localStorage
            const updatedDeviceType = { ...deviceType, name: deviceTypeName };
            setDeviceType(updatedDeviceType);
            localStorage.setItem('currentDeviceType', JSON.stringify(updatedDeviceType));
            setToast({
                show: true,
                message: 'Name updated successfully',
                type: 'success'
            });
        }
    };

    const handleGPSChange = async (enabled) => {
        setHasGPS(enabled);
        if (!enabled) {
            // If GPS is disabled, clear the paths
            setLatitudePath('');
            setLongitudePath('');
            setSosPath('');
            await saveDeviceTypeConfig({
                has_gps: false,
                latitude_path: '',
                longitude_path: '',
                sos_path: '',
            });
        } else {
            await saveDeviceTypeConfig({ has_gps: true });
        }
    };

    const handleJSONPathClick = async (path, type) => {
        // type puede ser: 'latitude', 'longitude', 'sos', 'battery', 'loraPayload', 'loraFport'
        // 'beaconList', 'beaconMac', 'beaconRssi'
        if (type === 'latitude') {
            setLatitudePath(path);
            await saveDeviceTypeConfig({ latitude_path: path });
            setToast({ show: true, message: t('adminDeviceTypes.latitudeConfigured'), type: 'success' });
        } else if (type === 'longitude') {
            setLongitudePath(path);
            await saveDeviceTypeConfig({ longitude_path: path });
            setToast({ show: true, message: t('adminDeviceTypes.longitudeConfigured'), type: 'success' });
        } else if (type === 'sos') {
            setSosPath(path);
            await saveDeviceTypeConfig({ sos_path: path });
            setToast({ show: true, message: `${t('adminDeviceTypes.sosConfigured')}: ${path}`, type: 'success' });
        } else if (type === 'battery') {
            setBatteryPath(path);
            await saveDeviceTypeConfig({ battery_path: path });
            setToast({ show: true, message: t('adminDeviceTypes.batteryConfigured'), type: 'success' });
        } else if (type === 'loraPayload') {
            setLoraPayloadPath(path);
            await saveDeviceTypeConfig({ lora_payload_path: path });
            setToast({ show: true, message: `Payload path configured: ${path}`, type: 'success' });
        } else if (type === 'loraFport') {
            setLoraFportPath(path);
            await saveDeviceTypeConfig({ lora_fport_path: path });
            setToast({ show: true, message: `fPort path configured: ${path}`, type: 'success' });
        } else if (type === 'beaconList') {
            setBeaconListPath(path);
            await saveDeviceTypeConfig({ beacon_list_path: path });
            setToast({ show: true, message: `Beacon list path configured: ${path}`, type: 'success' });
        } else if (type === 'beaconMac') {
            // Extract just the last key name from the full path (e.g. "beacons.0.mac" → "mac")
            const key = path.split('.').pop();
            setBeaconMacKey(key);
            await saveDeviceTypeConfig({ beacon_mac_key: key });
            setToast({ show: true, message: `MAC key configured: ${key}`, type: 'success' });
        } else if (type === 'beaconRssi') {
            const key = path.split('.').pop();
            setBeaconRssiKey(key);
            await saveDeviceTypeConfig({ beacon_rssi_key: key });
            setToast({ show: true, message: `RSSI key configured: ${key}`, type: 'success' });
        }
    };

    const runDecoderTest = () => {
        setTestDecoderError('');
        setTestDecoderSuccess(false);

        if (!loraDecoder.trim()) {
            setTestDecoderError('No decoder function defined. Write a decodeUplink function first.');
            return;
        }
        if (!testPayloadB64.trim()) {
            setTestDecoderError('Paste a base64 or hex payload to test.');
            return;
        }

        try {
            const raw = testPayloadB64.trim();
            let bytes;
            if (/^[0-9a-fA-F]+$/.test(raw) && raw.length % 2 === 0) {
                // Hex string (e.g. "010BB80264030C")
                bytes = [];
                for (let k = 0; k < raw.length; k += 2) {
                    bytes.push(parseInt(raw.substr(k, 2), 16));
                }
            } else {
                // Base64
                const binaryStr = atob(raw);
                bytes = Array.from(binaryStr, c => c.charCodeAt(0));
            }
            const fPort = parseInt(testFport, 10) || 1;

            // eslint-disable-next-line no-new-func
            const fn = new Function(`
                ${loraDecoder}
                return decodeUplink({ bytes: ${JSON.stringify(bytes)}, fPort: ${fPort} });
            `);
            const result = fn();

            if (!result || typeof result !== 'object' || !result.data) {
                setTestDecoderError('Decoder ran but did not return { data: { ... } }. Check your function.');
                return;
            }

            setDecoderResultData(result.data);
            saveDeviceTypeConfig({ decoder_last_result: JSON.stringify(result.data) });
            setTestDecoderSuccess(true);
        } catch (e) {
            setTestDecoderError('Error: ' + e.message);
        }
    };

    const handleBatteryChange = async (enabled) => {
        setHasBattery(enabled);
        if (!enabled) {
            setBatteryPath('');
            setBatteryIsPercentage(true);
            setBatteryMinVoltage('');
            setBatteryMaxVoltage('');
            await saveDeviceTypeConfig({
                has_battery: false,
                battery_path: '',
                battery_is_percentage: true,
                battery_min_voltage: '',
                battery_max_voltage: ''
            });
        } else {
            await saveDeviceTypeConfig({ has_battery: true });
        }
    };

    const handleBatteryPathChange = async (value) => {
        setBatteryPath(value);
        await saveDeviceTypeConfig({ battery_path: value });
    };

    const handleBatteryTypeChange = async (isPercentage) => {
        setBatteryIsPercentage(isPercentage);
        if (isPercentage) {
            // If percentage, clear voltages
            setBatteryMinVoltage('');
            setBatteryMaxVoltage('');
            await saveDeviceTypeConfig({
                battery_is_percentage: true,
                battery_min_voltage: '',
                battery_max_voltage: ''
            });
        } else {
            await saveDeviceTypeConfig({ battery_is_percentage: false });
        }
    };

    const handleBatteryVoltageChange = async (type, value) => {
        if (type === 'min') {
            setBatteryMinVoltage(value);
            await saveDeviceTypeConfig({ battery_min_voltage: value });
        } else {
            setBatteryMaxVoltage(value);
            await saveDeviceTypeConfig({ battery_max_voltage: value });
        }
    };

    const handlePredictiveMaintenanceChange = async (enabled) => {
        setIsPredictiveMaintenance(enabled);
        if (!enabled) {
            // If disabled, clear the selected system and topic
            setSelectedPMSystemId(null);
            await saveDeviceTypeConfig({
                is_predictive_maintenance: false,
                predictive_maintenance_system_id: null,
                mqtt_topic: null,
                mqtt_topic_description: null
            });
            setToast({
                show: true,
                message: 'Predictive maintenance disabled',
                type: 'success'
            });
        } else {
            // Save the current mqtt topic if exists
            const config = { is_predictive_maintenance: true };
            if (mqttTopic.trim()) {
                config.mqtt_topic = mqttTopic;
                config.mqtt_topic_description = topicDescription;
            }
            await saveDeviceTypeConfig(config);
            setToast({
                show: true,
                message: 'Predictive maintenance enabled. ' + (mqttTopic ? 'MQTT topic saved.' : 'Configure MQTT topic.'),
                type: 'info'
            });
        }
    };

    const handlePMSystemChange = async (systemId) => {
        const newSystemId = systemId ? parseInt(systemId) : null;
        setSelectedPMSystemId(newSystemId);
        await saveDeviceTypeConfig({ predictive_maintenance_system_id: newSystemId });

        const selectedSystem = predictiveMaintenanceSystems.find(s => s.id === newSystemId);
        setToast({
            show: true,
            message: selectedSystem
                ? `System "${selectedSystem.name}" configured`
                : 'Predictive maintenance system removed',
            type: 'success'
        });
    };

    const handleLoraChange = async (enabled) => {
        setIsEncrypted(enabled);
        if (!enabled) {
            setLoraPayloadPath('');
            setLoraFportPath('');
            setLoraDecoder('');
            await saveDeviceTypeConfig({
                is_encrypted: false,
                lora_payload_path: '',
                lora_fport_path: '',
                lora_decoder: ''
            });
        } else {
            // Mutually exclusive with Modbus
            setIsModbus(false);
            setModbusTransparent(false);
            await saveDeviceTypeConfig({ is_encrypted: true, is_modbus: false, modbus_transparent: false });
        }
    };

    const handleModbusChange = async (enabled) => {
        setIsModbus(enabled);
        if (!enabled) {
            setModbusTransparent(false);
            await saveDeviceTypeConfig({ is_modbus: false, modbus_transparent: false });
        } else {
            // Mutually exclusive with LoRa / Decoder
            setIsEncrypted(false);
            setLoraDecoder('');
            await saveDeviceTypeConfig({
                is_modbus: true,
                is_encrypted: false,
                lora_decoder: ''
            });
        }
    };

    const renderTopicSegments = () => {
        if (!mqttTopic) return null;

        const segments = mqttTopic.split('/');
        return (
            <div className={styles.topicSegments}>
                {segments.map((segment, index) => (
                    <span key={index}>
                        <span
                            className={`${styles.topicSegment} ${deviceIdSegmentIndex === index ? styles.selectedSegment : ''}`}
                            onClick={() => handleTopicSegmentClick(index)}
                            title="Click to mark as Device ID position"
                        >
                            {segment}
                        </span>
                        {index < segments.length - 1 && <span className={styles.topicSeparator}>/</span>}
                    </span>
                ))}
            </div>
        );
    };

    if (!deviceType) {
        return <div>Loading...</div>;
    }

    return (
        <div className={sidebarStyles.adminContent}>
            <AdminSidebar active={1} />

            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                duration={toast.duration || 3000}
                onClose={() => setToast({ ...toast, show: false })}
            />

            <Modal
                show={confirmModal.show}
                setter={(v) => setConfirmModal({ ...confirmModal, show: v })}
                name={confirmModal.title}
                buttonName={'Confirm'}
                onClick={confirmModal.onConfirm}
            >
                <p>{confirmModal.message}</p>
            </Modal>

            <Modal
                show={showSaveModal}
                setter={setShowSaveModal}
                name={'Confirm variable creation'}
                buttonName={'Create Variables'}
                onClick={confirmSave}
            >
                <p>{selectedPaths.length} variable(s) will be created for the topic:</p>
                <p><strong>{mqttTopic}</strong></p>
                <ul>
                    {selectedPaths.map(sp => (
                        <li key={sp.path}>
                            <strong>{sp.customName || sp.path}</strong> ({sp.type})
                            {sp.unit && ` - ${sp.unit}`}
                        </li>
                    ))}
                </ul>
            </Modal>

            <div className={'mx-5'}>
                <PageTitle
                    title={t('adminDeviceTypes.mqttConfig')}
                    hideButton={true}
                >
                    <div className="d-flex gap-2">
                        <button
                            className={`btn btn-outline-warning ${styles.clearButton}`}
                            onClick={clearForm}
                            title="Clear form to start with a new topic"
                        >
                            <FontAwesomeIcon icon={faBroom} className="me-2" />
                            Clear Form
                        </button>
                        <SecondaryButton
                            name={'Back'}
                            onClick={() => window.location.href = '/admin/device_types'}
                        />
                    </div>
                </PageTitle>

                {/* Inline name editor */}
                <div className={styles.nameEditor}>
                    <label className={styles.nameLabel}>Device Type Name:</label>
                    <div className="d-flex align-items-center gap-2">
                        {isEditingName ? (
                            <>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={deviceTypeName}
                                    onChange={(e) => setDeviceTypeName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleNameChange();
                                        }
                                    }}
                                    autoFocus
                                    style={{ maxWidth: '400px' }}
                                />
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={handleNameChange}
                                >
                                    <FontAwesomeIcon icon={faSave} />
                                </button>
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => {
                                        setIsEditingName(false);
                                        setDeviceTypeName(deviceType.name);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </>
                        ) : (
                            <>
                                <h5 className="mb-0">{deviceType.name}</h5>
                                <button
                                    className="btn btn-link btn-sm p-0"
                                    onClick={() => setIsEditingName(true)}
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="row mt-4">
                    {/* Left panel: Configuration */}
                    <div className="col-md-6">
                        <div className={styles.configPanel}>
                            <h5>1. Configure MQTT Topic</h5>
                            <div className="mb-3">
                                <label className={styles.label}>
                                    Topic MQTT (soporta wildcards +/#)
                                    {isPredictiveMaintenance && (
                                        <span className="badge bg-info ms-2">
                                            Predictive Maintenance - Auto-save enabled
                                        </span>
                                    )}
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Ej: kpdu/backend/+/imu_entry"
                                    value={mqttTopic}
                                    onChange={(e) => setMqttTopic(e.target.value)}
                                    onBlur={async () => {
                                        if (mqttTopic.trim()) {
                                            await saveDeviceTypeConfig({
                                                mqtt_topic: mqttTopic,
                                                mqtt_topic_description: topicDescription
                                            });
                                            setToast({
                                                show: true,
                                                message: 'MQTT topic saved',
                                                type: 'success'
                                            });
                                        }
                                    }}
                                />
                                <small className="text-muted">
                                    Use + for one level, # for multiple levels
                                    {isPredictiveMaintenance && (
                                        <span className="text-info d-block mt-1">
                                            ℹ️ For predictive maintenance, the topic is saved automatically (no variables required)
                                        </span>
                                    )}
                                </small>
                                {mqttTopic && !mqttTopic.includes('+') && !mqttTopic.includes('#') && (
                                    <div className="alert alert-warning py-1 px-2 mt-2 mb-0" style={{fontSize: '12px'}}>
                                        ⚠️ Topic has no wildcards (+ or #). If multiple devices share this type, each needs its own variable segment. Use + for one level (e.g.: MACHINE/+/data).
                                    </div>
                                )}
                            </div>

                            {mqttTopic && (
                                <div className="mb-3">
                                    <label className={styles.label}>
                                        Device ID Position in Topic
                                        {deviceIdSegmentIndex !== null && (
                                            <span className={styles.deviceIdBadge}>
                                                Segment {deviceIdSegmentIndex}
                                            </span>
                                        )}
                                    </label>
                                    {renderTopicSegments()}
                                    <small className="text-muted d-block mt-2">
                                        Click on the topic segment where the Device ID is located
                                        {deviceIdSegmentIndex !== null && (
                                            <span className="text-success">
                                                {' '}✓ Segment {deviceIdSegmentIndex} selected
                                            </span>
                                        )}
                                    </small>
                                </div>
                            )}

                            <div className="mb-3">
                                <label className={styles.label}>Description (optional)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="E.g.: IMU sensor data"
                                    value={topicDescription}
                                    onChange={(e) => setTopicDescription(e.target.value)}
                                    onBlur={async () => {
                                        if (mqttTopic.trim()) {
                                            await saveDeviceTypeConfig({
                                                mqtt_topic: mqttTopic,
                                                mqtt_topic_description: topicDescription
                                            });
                                        }
                                    }}
                                />
                            </div>

                            {/* GPS Configuration */}
                            <h5 className="mt-4">2. GPS Configuration</h5>
                            <div className="mb-3">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="hasGPS"
                                        checked={hasGPS}
                                        onChange={(e) => handleGPSChange(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="hasGPS">
                                        Does the device have GPS signal?
                                    </label>
                                </div>

                                {hasGPS && (decoderResultData || parsedJSON) && (
                                    <div className="mt-3">
                                        <div className="mb-3">
                                            <label className={styles.label}>
                                                Latitude {latitudePath && <span className={styles.pathBadge}>{latitudePath}</span>}
                                            </label>
                                            <small className="d-block text-muted mb-2">Click on the JSON field that contains the latitude</small>
                                            <JSONViewer
                                                json={decoderResultData || parsedJSON}
                                                selectedPaths={latitudePath ? [latitudePath] : []}
                                                onSelect={(path) => handleJSONPathClick(path, 'latitude')}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className={styles.label}>
                                                Longitude {longitudePath && <span className={styles.pathBadge}>{longitudePath}</span>}
                                            </label>
                                            <small className="d-block text-muted mb-2">Click on the JSON field that contains the longitude</small>
                                            <JSONViewer
                                                json={decoderResultData || parsedJSON}
                                                selectedPaths={longitudePath ? [longitudePath] : []}
                                                onSelect={(path) => handleJSONPathClick(path, 'longitude')}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className={styles.label}>
                                                {t('adminDeviceTypes.sosPathLabel')} {sosPath && <span className={styles.pathBadge}>{sosPath}</span>}
                                            </label>
                                            <small className="d-block text-muted mb-2">{t('adminDeviceTypes.sosPathHint')}</small>
                                            <JSONViewer
                                                json={decoderResultData || parsedJSON}
                                                selectedPaths={sosPath ? [sosPath] : []}
                                                onSelect={(path) => handleJSONPathClick(path, 'sos')}
                                            />
                                        </div>
                                    </div>
                                )}
                                {hasGPS && !(decoderResultData || parsedJSON) && (
                                    <div className="mt-2">
                                        {(latitudePath || longitudePath || sosPath) ? (
                                            <div className="p-2 rounded" style={{backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: '13px'}}>
                                                <div className="text-muted mb-1">Configured paths:</div>
                                                {latitudePath && <div>Latitude: <code>{latitudePath}</code></div>}
                                                {longitudePath && <div>Longitude: <code>{longitudePath}</code></div>}
                                                {sosPath && <div>SOS field: <code>{sosPath}</code></div>}
                                                <small className="text-muted d-block mt-1">Paste an example JSON below to change these paths</small>
                                            </div>
                                        ) : (
                                            <div className={styles.emptyState}>First paste an example JSON below</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Indoor Positioning Configuration (shown when has_gps=true) */}
                            {hasGPS && (
                                <div className="mb-3 mt-3 p-3 rounded" style={{ border: '1px solid #CBD5E0', backgroundColor: '#F7FAFC' }}>
                                    <label className={`${styles.label} mb-3 d-block`}>Indoor Positioning (BLE Beacons) (optional)</label>
                                    <small className="d-block text-muted mb-3">
                                        Configure the JSON paths for BLE beacon scanning data. When beacons are present in the payload, indoor positioning will be used instead of GPS.
                                    </small>

                                    {(decoderResultData || parsedJSON) ? (
                                        <>
                                            <div className="mb-3">
                                                <label className={styles.label}>
                                                    Beacon List Path {beaconListPath && <span className={styles.pathBadge}>{beaconListPath}</span>}
                                                </label>
                                                <small className="d-block text-muted mb-2">Click on the array field that contains the beacon list</small>
                                                <JSONViewer
                                                    json={decoderResultData || parsedJSON}
                                                    selectedPaths={beaconListPath ? [beaconListPath] : []}
                                                    onSelect={(path) => handleJSONPathClick(path, 'beaconList')}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className={styles.label}>
                                                    MAC Address Key {beaconMacKey && <span className={styles.pathBadge}>{beaconMacKey}</span>}
                                                </label>
                                                <small className="d-block text-muted mb-2">Click on the MAC field inside a beacon object</small>
                                                <JSONViewer
                                                    json={decoderResultData || parsedJSON}
                                                    selectedPaths={beaconMacKey && beaconListPath ? [`${beaconListPath}.0.${beaconMacKey}`] : []}
                                                    onSelect={(path) => handleJSONPathClick(path, 'beaconMac')}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className={styles.label}>
                                                    RSSI Key {beaconRssiKey && <span className={styles.pathBadge}>{beaconRssiKey}</span>}
                                                </label>
                                                <small className="d-block text-muted mb-2">Click on the RSSI field inside a beacon object</small>
                                                <JSONViewer
                                                    json={decoderResultData || parsedJSON}
                                                    selectedPaths={beaconRssiKey && beaconListPath ? [`${beaconListPath}.0.${beaconRssiKey}`] : []}
                                                    onSelect={(path) => handleJSONPathClick(path, 'beaconRssi')}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {(beaconListPath || beaconMacKey !== 'mac' || beaconRssiKey !== 'rssi') ? (
                                                <div className="p-2 rounded mb-3" style={{backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: '13px'}}>
                                                    <div className="text-muted mb-1">Configured paths:</div>
                                                    {beaconListPath && <div>Beacon list: <code>{beaconListPath}</code></div>}
                                                    {beaconMacKey && <div>MAC key: <code>{beaconMacKey}</code></div>}
                                                    {beaconRssiKey && <div>RSSI key: <code>{beaconRssiKey}</code></div>}
                                                    <small className="text-muted d-block mt-1">Paste an example JSON below to change these paths</small>
                                                </div>
                                            ) : (
                                                <div className={styles.emptyState}>First paste an example JSON below</div>
                                            )}
                                            <div className="row">
                                                <div className="col-md-4 mb-2">
                                                    <label className={styles.label}>Beacon List Path</label>
                                                    <input type="text" className="form-control" placeholder="e.g. beacons"
                                                        value={beaconListPath} onChange={(e) => setBeaconListPath(e.target.value)}
                                                        onBlur={async () => { await saveDeviceTypeConfig({ beacon_list_path: beaconListPath }); }} />
                                                </div>
                                                <div className="col-md-4 mb-2">
                                                    <label className={styles.label}>MAC Address Key</label>
                                                    <input type="text" className="form-control" placeholder="mac"
                                                        value={beaconMacKey} onChange={(e) => setBeaconMacKey(e.target.value)}
                                                        onBlur={async () => { await saveDeviceTypeConfig({ beacon_mac_key: beaconMacKey }); }} />
                                                </div>
                                                <div className="col-md-4 mb-2">
                                                    <label className={styles.label}>RSSI Key</label>
                                                    <input type="text" className="form-control" placeholder="rssi"
                                                        value={beaconRssiKey} onChange={(e) => setBeaconRssiKey(e.target.value)}
                                                        onBlur={async () => { await saveDeviceTypeConfig({ beacon_rssi_key: beaconRssiKey }); }} />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Battery Configuration */}
                            <h5 className="mt-4">3. Battery Configuration</h5>
                            <div className="mb-3">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="hasBattery"
                                        checked={hasBattery}
                                        onChange={(e) => handleBatteryChange(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="hasBattery">
                                        Does the device use battery?
                                    </label>
                                </div>

                                {hasBattery && (decoderResultData || parsedJSON) && (
                                    <div className="mt-3">
                                        <div className="mb-3">
                                            <label className={styles.label}>
                                                Battery field {batteryPath && <span className={styles.pathBadge}>{batteryPath}</span>}
                                            </label>
                                            <small className="d-block text-muted mb-2">Click on the JSON field that contains the battery level</small>
                                            <JSONViewer
                                                json={decoderResultData || parsedJSON}
                                                selectedPaths={batteryPath ? [batteryPath] : []}
                                                onSelect={(path) => handleJSONPathClick(path, 'battery')}
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label className={styles.label}>Value type</label>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="batteryType"
                                                    id="batteryPercentage"
                                                    checked={batteryIsPercentage}
                                                    onChange={() => handleBatteryTypeChange(true)}
                                                />
                                                <label className="form-check-label" htmlFor="batteryPercentage">
                                                    Percentage (0-100%)
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="batteryType"
                                                    id="batteryVoltage"
                                                    checked={!batteryIsPercentage}
                                                    onChange={() => handleBatteryTypeChange(false)}
                                                />
                                                <label className="form-check-label" htmlFor="batteryVoltage">
                                                    Voltage (requires conversion to %)
                                                </label>
                                            </div>
                                        </div>

                                        {!batteryIsPercentage && (
                                            <div className="row">
                                                <div className="col-6">
                                                    <label className={styles.label}>Minimum Voltage (V)</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        className="form-control form-control-sm"
                                                        placeholder="e.g.: 3.0"
                                                        value={batteryMinVoltage}
                                                        onChange={(e) => handleBatteryVoltageChange('min', e.target.value)}
                                                    />
                                                    <small className="text-muted">Battery discharged</small>
                                                </div>
                                                <div className="col-6">
                                                    <label className={styles.label}>Maximum Voltage (V)</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        className="form-control form-control-sm"
                                                        placeholder="e.g.: 4.2"
                                                        value={batteryMaxVoltage}
                                                        onChange={(e) => handleBatteryVoltageChange('max', e.target.value)}
                                                    />
                                                    <small className="text-muted">Battery charged</small>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {hasBattery && !(decoderResultData || parsedJSON) && (
                                    <div className="mt-2">
                                        {batteryPath ? (
                                            <div className="p-2 rounded" style={{backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: '13px'}}>
                                                <div className="text-muted mb-1">Configured paths:</div>
                                                <div>Battery: <code>{batteryPath}</code></div>
                                                <small className="text-muted d-block mt-1">Paste an example JSON below to change this path</small>
                                            </div>
                                        ) : (
                                            <div className={styles.emptyState}>First paste an example JSON below</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Predictive Maintenance Configuration */}
                            <h5 className="mt-4">4. Predictive Maintenance</h5>
                            <div className="mb-3">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="isPredictiveMaintenance"
                                        checked={isPredictiveMaintenance}
                                        onChange={(e) => handlePredictiveMaintenanceChange(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="isPredictiveMaintenance">
                                        Is this a predictive maintenance device?
                                    </label>
                                </div>
                                <small className="text-muted d-block mt-1">
                                    Enable this option if the device has a special predictive maintenance dashboard (e.g.: BOB ASSISTANT)
                                </small>

                                {isPredictiveMaintenance && (
                                    <div className="mt-3">
                                        <label className={styles.label}>Predictive Maintenance System</label>
                                        <select
                                            className="form-select"
                                            value={selectedPMSystemId || ''}
                                            onChange={(e) => handlePMSystemChange(e.target.value)}
                                        >
                                            <option value="">-- Select system --</option>
                                            {predictiveMaintenanceSystems.map(system => (
                                                <option key={system.id} value={system.id}>
                                                    {system.name}
                                                </option>
                                            ))}
                                        </select>
                                        {selectedPMSystemId && (
                                            <div className="mt-2 p-2" style={{
                                                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(99, 102, 241, 0.3)'
                                            }}>
                                                {predictiveMaintenanceSystems.find(s => s.id === selectedPMSystemId)?.description || ''}
                                            </div>
                                        )}
                                        {predictiveMaintenanceSystems.length === 0 && (
                                            <small className="text-warning d-block mt-2">
                                                No predictive maintenance systems registered. Run backend migrations.
                                            </small>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* LoRa / Decoder Configuration */}
                            <h5 className="mt-4">5. LoRa / Decoder</h5>
                            <div className="mb-3">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="isEncrypted"
                                        checked={isEncrypted}
                                        onChange={(e) => handleLoraChange(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="isEncrypted">
                                        LoRa / Encrypted Payload
                                    </label>
                                </div>
                                <small className="text-muted d-block mt-1">
                                    Enable if this device sends LoRa payloads encoded in base64 that need a JS decoder (e.g. via The Things Network)
                                </small>

                                {isEncrypted && (
                                    <div className="mt-3">
                                        <div className="mb-3">
                                            <label className={styles.label}>
                                                Payload path (base64 bytes) {loraPayloadPath && <span className={styles.pathBadge}>{loraPayloadPath}</span>}
                                            </label>
                                            <small className="d-block text-muted mb-2">Click on the JSON field that contains the base64-encoded bytes</small>
                                            {parsedJSON ? (
                                                <JSONViewer
                                                    json={parsedJSON}
                                                    selectedPaths={loraPayloadPath ? [loraPayloadPath] : []}
                                                    onSelect={(path) => handleJSONPathClick(path, 'loraPayload')}
                                                />
                                            ) : (
                                                loraPayloadPath ? (
                                                    <div className="p-2 rounded" style={{backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: '13px'}}>
                                                        <code>{loraPayloadPath}</code>
                                                        <small className="text-muted d-block mt-1">Paste an example JSON below to change this path</small>
                                                    </div>
                                                ) : (
                                                    <div className={styles.emptyState}>First paste an example JSON below</div>
                                                )
                                            )}
                                        </div>
                                        <div className="mb-3">
                                            <label className={styles.label}>
                                                fPort path (optional) {loraFportPath && <span className={styles.pathBadge}>{loraFportPath}</span>}
                                            </label>
                                            <small className="d-block text-muted mb-2">Click on the JSON field that contains the LoRaWAN port number. Leave unset to use port 0.</small>
                                            {parsedJSON ? (
                                                <JSONViewer
                                                    json={parsedJSON}
                                                    selectedPaths={loraFportPath ? [loraFportPath] : []}
                                                    onSelect={(path) => handleJSONPathClick(path, 'loraFport')}
                                                />
                                            ) : (
                                                loraFportPath ? (
                                                    <div className="p-2 rounded" style={{backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: '13px'}}>
                                                        <code>{loraFportPath}</code>
                                                        <small className="text-muted d-block mt-1">Paste an example JSON below to change this path</small>
                                                    </div>
                                                ) : (
                                                    <div className={styles.emptyState}>First paste an example JSON below</div>
                                                )
                                            )}
                                        </div>
                                        <div className="mb-2">
                                            <label className={styles.label}>Decoder function (JavaScript)</label>
                                            <textarea
                                                className="form-control"
                                                style={{ fontFamily: 'monospace', fontSize: '13px', minHeight: '280px' }}
                                                placeholder={'function decodeUplink(input) {\n  // input.bytes  \u2192 array of byte values\n  // input.fPort  \u2192 LoRa port number\n  return {\n    data: {\n      // your fields\n    }\n  };\n}'}
                                                value={loraDecoder}
                                                onChange={(e) => setLoraDecoder(e.target.value)}
                                                onBlur={async () => {
                                                    await saveDeviceTypeConfig({ lora_decoder: loraDecoder });
                                                }}
                                            />
                                            <small className="text-muted">
                                                Must define <code>decodeUplink(input)</code> returning <code>{'{ data: { ... } }'}</code>. Compatible with TTN codecs.
                                            </small>
                                        </div>
                                        {/* Test Decoder */}
                                        <div className="mt-3 p-3 rounded" style={{border: '1px solid #CBD5E0', backgroundColor: '#F7FAFC'}}>
                                            <div className="d-flex align-items-center mb-2" style={{gap: '8px'}}>
                                                <strong style={{fontSize: '13px'}}>Test Decoder</strong>
                                                <small className="text-muted">Run your decoder with a sample payload and preview the output</small>
                                            </div>
                                            <div className="d-flex align-items-end" style={{gap: '8px'}}>
                                                <div style={{flex: 1}}>
                                                    <label style={{fontSize: '12px', fontWeight: 600, color: '#4A5568', marginBottom: '4px', display: 'block'}}>
                                                        Payload (base64)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        style={{fontFamily: 'monospace', fontSize: '12px'}}
                                                        placeholder="base64 (e.g. AQMEAAoAFA==) or hex (e.g. 010BB80264030C)"
                                                        value={testPayloadB64}
                                                        onChange={e => { setTestPayloadB64(e.target.value); setTestDecoderError(''); setTestDecoderSuccess(false); }}
                                                    />
                                                </div>
                                                <div style={{width: '80px'}}>
                                                    <label style={{fontSize: '12px', fontWeight: 600, color: '#4A5568', marginBottom: '4px', display: 'block'}}>
                                                        fPort
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        min="1" max="255"
                                                        value={testFport}
                                                        onChange={e => setTestFport(e.target.value)}
                                                    />
                                                </div>
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={runDecoderTest}
                                                    style={{whiteSpace: 'nowrap', marginBottom: '1px'}}
                                                >
                                                    ▶ Run
                                                </button>
                                            </div>
                                            {testDecoderError && (
                                                <div className="alert alert-danger mt-2 mb-0 py-1 px-2" style={{fontSize: '12px'}}>
                                                    {testDecoderError}
                                                </div>
                                            )}
                                            {testDecoderSuccess && (
                                                <div className="alert alert-success mt-2 mb-0 py-1 px-2" style={{fontSize: '12px'}}>
                                                    ✓ Decoder ran successfully. Scroll down to <strong>section 8</strong> to select variables from the output.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modbus RTU / RAK7431 Configuration */}
                            <h5 className="mt-4">6. Modbus RTU / RAK7431</h5>
                            <div className="mb-3">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="isModbus"
                                        checked={isModbus}
                                        onChange={(e) => handleModbusChange(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="isModbus">
                                        Modbus RTU over LoRaWAN (RAK7431)
                                    </label>
                                </div>
                                <small className="text-muted d-block mt-1">
                                    Enable if this device is a RAK7431 or similar Modbus-to-LoRaWAN bridge. Mutually exclusive with LoRa/Decoder.
                                </small>

                                {isModbus && (
                                    <div className="mt-3">
                                        <div className="mb-3">
                                            <label className={styles.label}>
                                                Payload path (raw bytes) {loraPayloadPath && <span className={styles.pathBadge}>{loraPayloadPath}</span>}
                                            </label>
                                            <small className="d-block text-muted mb-2">Click on the JSON field that contains the encoded Modbus bytes (base64 or hex)</small>
                                            {parsedJSON ? (
                                                <JSONViewer
                                                    json={parsedJSON}
                                                    selectedPaths={loraPayloadPath ? [loraPayloadPath] : []}
                                                    onSelect={(path) => handleJSONPathClick(path, 'loraPayload')}
                                                />
                                            ) : (
                                                loraPayloadPath ? (
                                                    <div className="p-2 rounded" style={{backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: '13px'}}>
                                                        <code>{loraPayloadPath}</code>
                                                        <small className="text-muted d-block mt-1">Paste an example JSON below to change this path</small>
                                                    </div>
                                                ) : (
                                                    <div className={styles.emptyState}>First paste an example JSON below</div>
                                                )
                                            )}
                                        </div>
                                        <div className="mb-3">
                                            <label className={styles.label}>
                                                fPort path {loraFportPath && <span className={styles.pathBadge}>{loraFportPath}</span>}
                                            </label>
                                            <small className="d-block text-muted mb-2">Click on the JSON field that contains the LoRaWAN port / fPort (used to match poll task IDs)</small>
                                            {parsedJSON ? (
                                                <JSONViewer
                                                    json={parsedJSON}
                                                    selectedPaths={loraFportPath ? [loraFportPath] : []}
                                                    onSelect={(path) => handleJSONPathClick(path, 'loraFport')}
                                                />
                                            ) : (
                                                loraFportPath ? (
                                                    <div className="p-2 rounded" style={{backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: '13px'}}>
                                                        <code>{loraFportPath}</code>
                                                        <small className="text-muted d-block mt-1">Paste an example JSON below to change this path</small>
                                                    </div>
                                                ) : (
                                                    <div className={styles.emptyState}>First paste an example JSON below</div>
                                                )
                                            )}
                                        </div>

                                        <div className="form-check form-switch mt-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="modbusTransparent"
                                                checked={modbusTransparent}
                                                onChange={async (e) => {
                                                    setModbusTransparent(e.target.checked);
                                                    await saveDeviceTypeConfig({ modbus_transparent: e.target.checked });
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor="modbusTransparent">
                                                Transparent Mode (<code>AT+TRANSPARENT=1</code>)
                                            </label>
                                        </div>
                                        <div className="alert alert-info mt-2 py-2" style={{ fontSize: '0.85rem' }}>
                                            {modbusTransparent ? (
                                                <>
                                                    <strong>Transparent mode:</strong> The payload bytes are raw Modbus RTU directly. fPort is used as the poll ID to match variables.
                                                </>
                                            ) : (
                                                <>
                                                    <strong>Non-transparent mode (default RAK7431):</strong> The payload uses Appendix II encapsulation — <code>DTU_CMD + MSER + MDATA_LEN + TASK_ID + Modbus RTU</code>. The TASK_ID is extracted automatically and used to match variables.
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <h5 className="mt-4">7. Paste Example JSON</h5>
                            <div className="mb-3">
                                <textarea
                                    className={`form-control ${styles.jsonInput}`}
                                    placeholder='Paste the JSON payload here...'
                                    value={examplePayload}
                                    onChange={(e) => handlePayloadChange(e.target.value)}
                                    onBlur={async () => {
                                        if (!jsonError && examplePayload.trim()) {
                                            await saveDeviceTypeConfig({ example_payload: examplePayload });
                                        }
                                    }}
                                    rows={10}
                                />
                                {jsonError && (
                                    <div className="alert alert-danger mt-2">{jsonError}</div>
                                )}
                            </div>

                            <h5 className="mt-4">8. Select Variables from JSON</h5>
                            {(decoderResultData || parsedJSON) ? (
                                <JSONViewer
                                    json={decoderResultData || parsedJSON}
                                    selectedPaths={selectedPaths.map(p => p.path)}
                                    onSelect={handlePropertySelect}
                                />
                            ) : (
                                <div className={styles.emptyState}>
                                    Paste a valid JSON to start selecting variables
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right panel: Selected variables */}
                    <div className="col-md-6">
                        <div className={styles.selectedPanel}>
                            <h5>
                                {editingVariableId ? (
                                    <span>
                                        <FontAwesomeIcon icon={faEdit} className="me-2 text-primary" />
                                        Editing Variable
                                    </span>
                                ) : (
                                    `Selected Variables (${selectedPaths.length})`
                                )}
                            </h5>

                            {selectedPaths.length > 0 ? (
                                <>
                                    <div className={styles.selectedList}>
                                        {selectedPaths.map(selected => (
                                            <div key={selected.path} className={styles.selectedItem}>
                                                <div className={styles.selectedHeader}>
                                                    <span className={styles.pathBadge}>{selected.path}</span>
                                                    <button
                                                        className={styles.removeBtn}
                                                        onClick={() => removeSelectedPath(selected.path)}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>

                                                <div className="mt-2">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm mb-2"
                                                        placeholder="Variable name (optional)"
                                                        value={selected.customName || ''}
                                                        onChange={(e) => updateSelectedPathMetadata(selected.path, 'customName', e.target.value)}
                                                    />

                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Unit (e.g.: °C, rpm, m/s²)"
                                                        value={selected.unit || ''}
                                                        onChange={(e) => updateSelectedPathMetadata(selected.path, 'unit', e.target.value)}
                                                    />
                                                </div>

                                                <div className="mt-2">
                                                    {(() => {
                                                        const activeType = selected.overrideType || selected.type;
                                                        // Boolean is offered when:
                                                        //   - number field with value 0 or 1 (cast candidate)
                                                        //   - actual JSON boolean (true/false)
                                                        //   - already saved as boolean
                                                        const isNumericBoolCandidate = (selected.value === 0 || selected.value === 1);
                                                        const isActualBool = typeof selected.value === 'boolean';
                                                        const isBoolCandidate = isNumericBoolCandidate || isActualBool;
                                                        const showBoolOption = isBoolCandidate || activeType === 'boolean';
                                                        const boolLabel = isActualBool
                                                            ? 'Boolean (true / false)'
                                                            : 'Boolean (1→true / 0→false)';
                                                        return (
                                                            <>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={activeType}
                                                                    onChange={(e) => updateSelectedPathMetadata(selected.path, 'overrideType', e.target.value)}
                                                                >
                                                                    {(typeof selected.value === 'number' || (selected.value === null && selected.type === 'number')) && <option value="number">Number</option>}
                                                                    {showBoolOption && <option value="boolean">{boolLabel}</option>}
                                                                    <option value="string">String</option>
                                                                </select>
                                                                {isBoolCandidate && activeType !== 'boolean' && (
                                                                    <small className="text-warning d-block mt-1">
                                                                        Value is {JSON.stringify(selected.value)} — you can switch to Boolean
                                                                    </small>
                                                                )}
                                                                {selected.overrideType && selected.overrideType !== selected.type && (
                                                                    <small className="text-info d-block mt-1">
                                                                        Auto-detected: {selected.type} → overridden to: {selected.overrideType}
                                                                    </small>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                                <div className={styles.typeInfo}>
                                                    Example value: <code>{JSON.stringify(selected.value)}</code>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-3">
                                        {editingVariableId ? (
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-success flex-fill"
                                                    onClick={updateVariable}
                                                >
                                                    <FontAwesomeIcon icon={faSave} className="me-2" />
                                                    Save Changes
                                                </button>
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={cancelEdit}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            </div>
                                        ) : (
                                            <PrimaryButton
                                                name={`Create ${selectedPaths.length} Variable(s)`}
                                                onClick={saveConfiguration}
                                            />
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.emptyState}>
                                    Click on JSON properties to select them
                                </div>
                            )}

                            <hr className="my-4" />

                            {(() => {
                                const modbusVars = variables.filter(v => v.modbus_register_address != null || v.modbus_data_type);
                                const jsonVars = variables.filter(v => !v.modbus_register_address && !v.modbus_data_type);

                                const renderVariable = (variable) => (
                                    <div
                                        key={variable.id}
                                        className={`${styles.existingItem} ${editingVariableId === variable.id ? styles.editingItem : ''}`}
                                    >
                                        <div
                                            className={styles.variableInfo}
                                            onClick={() => loadVariableForEdit(variable)}
                                            style={{ cursor: 'pointer', flex: 1 }}
                                        >
                                            <div className={styles.variableName}>
                                                {variable.name}
                                                {editingVariableId === variable.id && (
                                                    <span className={styles.editingBadge}>
                                                        <FontAwesomeIcon icon={faEdit} className="ms-2" />
                                                        Editing
                                                    </span>
                                                )}
                                            </div>
                                            {variable.modbus_register_address != null ? (
                                                <div className={styles.variablePath} style={{color: '#7c3aed'}}>
                                                    Reg {variable.modbus_register_address} · {variable.modbus_data_type}
                                                    {variable.modbus_poll_id != null && ` · Poll ID ${variable.modbus_poll_id}`}
                                                </div>
                                            ) : (
                                                <div className={styles.variablePath}>{variable.json_path}</div>
                                            )}
                                            {!variable.modbus_register_address && variable.mqtt_topic && (
                                                <div className={styles.variableTopic}>Topic: {variable.mqtt_topic}</div>
                                            )}
                                            {variable.unit && (
                                                <div className={styles.variableUnit}>Unit: {variable.unit}</div>
                                            )}
                                        </div>
                                        <button
                                            className={styles.deleteBtn}
                                            onClick={(e) => { e.stopPropagation(); deleteVariable(variable.id); }}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                );

                                return (
                                    <>
                                        {modbusVars.length > 0 && (
                                            <div className="mb-3">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <h5 className="mb-0" style={{color: '#7c3aed'}}>
                                                        Modbus Variables ({modbusVars.length})
                                                    </h5>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => { window.location.href = '/admin/device_types/variables'; }}
                                                    >
                                                        Manage Variables
                                                    </button>
                                                </div>
                                                <div className={styles.existingVariables}>
                                                    {modbusVars.map(renderVariable)}
                                                </div>
                                            </div>
                                        )}

                                        {jsonVars.length > 0 && (
                                            <div className="mb-3">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <h5 className="mb-0">
                                                        JSON Variables ({jsonVars.length})
                                                    </h5>
                                                    <div className="d-flex gap-2">
                                                        {mqttTopic && jsonVars.some(v => v.mqtt_topic !== mqttTopic) && (
                                                            <button
                                                                className="btn btn-sm btn-outline-warning"
                                                                onClick={updateAllVariableTopics}
                                                                title="Update the MQTT topic for all existing variables to the current topic"
                                                            >
                                                                Update topic for all variables
                                                            </button>
                                                        )}
                                                        {modbusVars.length === 0 && (
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => { window.location.href = '/admin/device_types/variables'; }}
                                                            >
                                                                Manage Variables
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={styles.existingVariables}>
                                                    {jsonVars.map(renderVariable)}
                                                </div>
                                            </div>
                                        )}

                                        {variables.length === 0 && (
                                            <div>
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <h5 className="mb-0">Existing Variables (0)</h5>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => { window.location.href = '/admin/device_types/variables'; }}
                                                    >
                                                        Manage Variables
                                                    </button>
                                                </div>
                                                <div className={styles.existingVariables}>
                                                    <div className={styles.emptyState}>No variables configured yet</div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>

                        {/* ── Default Action Buttons / Sliders ── */}
                        <div style={{marginTop: '32px', background: '#fff', borderRadius: '12px', padding: '24px', border: '1px solid #E2E8F0'}}>
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px'}}>
                                <div style={{fontWeight: '600', fontSize: '15px', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px'}}>
                                    <FontAwesomeIcon icon={faBolt} color={'#4F46E5'}/>
                                    {t('adminDeviceTypes.defaultButtonsTitle')}
                                </div>
                                <button
                                    onClick={openNewBtnTemplate}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        padding: '6px 14px', borderRadius: '8px',
                                        border: '1px solid #C7D2FE', background: '#EEF2FF',
                                        cursor: 'pointer', color: '#4F46E5', fontSize: '13px', fontWeight: '500',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus}/>
                                    {t('adminDeviceTypes.add')}
                                </button>
                            </div>
                            <p style={{fontSize: '12px', color: '#94A3B8', marginBottom: '16px'}}>
                                {t('adminDeviceTypes.btnTemplatesDesc')}
                            </p>

                            {btnTemplates.length === 0 ? (
                                <div style={{fontSize: '13px', color: '#CBD5E1', textAlign: 'center', padding: '20px'}}>{t('adminDeviceTypes.noTemplatesConfigured')}</div>
                            ) : (
                                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                    {btnTemplates.map(tmpl => (
                                        <div key={tmpl.id} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '10px 14px', borderRadius: '8px',
                                            background: '#F8FAFC', border: '1px solid #E2E8F0',
                                        }}>
                                            <div>
                                                <span style={{fontWeight: '600', fontSize: '13px', color: '#334155'}}>{tmpl.name}</span>
                                                <span style={{
                                                    marginLeft: '8px', fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                                                    background: tmpl.button_type === 'slider' ? '#EEF2FF' : '#F0FDF4',
                                                    color: tmpl.button_type === 'slider' ? '#4F46E5' : '#16A34A',
                                                }}>
                                                    {tmpl.button_type === 'slider' ? t('adminDeviceTypes.sliderControl') : t('adminDeviceTypes.buttonTypeBadge')}
                                                </span>
                                                <span style={{marginLeft: '8px', fontSize: '11px', color: '#94A3B8'}}>
                                                    {tmpl.action_type === 5 ? 'MQTT' : 'API'}
                                                </span>
                                                {tmpl.linked_variable_name && (
                                                    <span style={{marginLeft: '8px', fontSize: '11px', color: '#64748B'}}>
                                                        → {tmpl.linked_variable_name}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{display: 'flex', gap: '10px'}}>
                                                <FontAwesomeIcon icon={faPencil} color={'#94A3B8'} style={{cursor: 'pointer'}} onClick={() => openEditBtnTemplate(tmpl)}/>
                                                <FontAwesomeIcon icon={faTrash} color={'#CBD5E1'} style={{cursor: 'pointer'}} onClick={() => deleteBtnTemplate(tmpl.id)}/>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Button Template Modal */}
            <Modal show={showBtnModal} setter={(v) => { if (!v) closeBtnModal(); }} name={editingBtnId ? t('adminDeviceTypes.editTemplate') : t('adminDeviceTypes.newTemplate')} buttonName={editingBtnId ? t('adminDeviceTypes.save') : t('adminDeviceTypes.create')} onClick={saveBtnTemplate}>
                <div style={{marginBottom: '10px'}}>
                    <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', marginBottom: '4px', display: 'block'}}>{t('adminDeviceTypes.nameField')}</label>
                    <input
                        value={btnForm.name}
                        onChange={e => { setBtnForm({...btnForm, name: e.target.value}); if (e.target.value.trim()) setBtnFormError(''); }}
                        placeholder="e.g. Encender"
                        style={{width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid ${btnFormError ? '#EF4444' : '#E2E8F0'}`, fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box'}}
                    />
                    {btnFormError && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '4px'}}>{btnFormError}</div>}
                </div>

                <div style={{marginBottom: '10px'}}>
                    <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', marginBottom: '4px', display: 'block'}}>{t('adminDeviceTypes.controlType')}</label>
                    <div style={{display: 'flex', gap: '8px'}}>
                        {[
                            {v: 'button', l: t('adminDeviceTypes.buttonControl'), icon: faBolt},
                            {v: 'toggle', l: t('adminDeviceTypes.toggleControl'), icon: faToggleOn},
                            {v: 'slider', l: t('adminDeviceTypes.sliderControl'), icon: faSlidersH},
                            {v: 'text', l: t('adminDeviceTypes.textControl'), icon: faFont},
                        ].map(opt => (
                            <label key={opt.v} style={{
                                display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                                padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '500',
                                border: `1px solid ${btnForm.button_type === opt.v ? '#C7D2FE' : '#E2E8F0'}`,
                                backgroundColor: btnForm.button_type === opt.v ? '#EEF2FF' : '#F8FAFC',
                                color: btnForm.button_type === opt.v ? '#4F46E5' : '#64748B',
                            }}>
                                <input type="radio" value={opt.v} checked={btnForm.button_type === opt.v} onChange={() => setBtnForm({...btnForm, button_type: opt.v})} style={{display: 'none'}}/>
                                <FontAwesomeIcon icon={opt.icon} style={{fontSize: '11px'}}/>
                                {opt.l}
                            </label>
                        ))}
                    </div>
                </div>

                {btnForm.button_type === 'slider' && (
                    <div style={{display: 'flex', gap: '10px', marginBottom: '10px'}}>
                        <div style={{flex: 1}}>
                            <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px'}}>{t('adminDeviceTypes.sliderMin')}</label>
                            <input type="number" value={btnForm.slider_min} onChange={e => setBtnForm({...btnForm, slider_min: e.target.value})} style={{width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box'}}/>
                        </div>
                        <div style={{flex: 1}}>
                            <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px'}}>{t('adminDeviceTypes.sliderMax')}</label>
                            <input type="number" value={btnForm.slider_max} onChange={e => setBtnForm({...btnForm, slider_max: e.target.value})} placeholder={t('adminDeviceTypes.noLimit')} style={{width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box'}}/>
                        </div>
                    </div>
                )}

                <div style={{marginBottom: '10px'}}>
                    <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px'}}>{t('adminDeviceTypes.actionType')}</label>
                    <select value={btnForm.action_type} onChange={e => setBtnForm({...btnForm, action_type: e.target.value})} style={{width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box', marginBottom: '10px'}}>
                        <option value="5">MQTT</option>
                        <option value="6">API</option>
                    </select>
                </div>

                {(() => {
                    const btnPlaceholders = [
                        '{device_serial}',
                        ...variables.map(v => `{${v.name}}`),
                        ...(btnForm.button_type === 'toggle' ? ['{toggle_value}'] : []),
                        ...(btnForm.button_type === 'text' ? ['{text_value}', '"{text_value}"'] : []),
                        ...(btnForm.button_type === 'slider' ? ['{slider_value}'] : []),
                    ];
                    return (<>
                    {parseInt(btnForm.action_type) === 5 && (
                        <>
                            <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px'}}>{t('adminDeviceTypes.mqttConnection')}</label>
                            <select value={btnForm.mqtt_connection} onChange={e => setBtnForm({...btnForm, mqtt_connection: e.target.value})} style={{width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box', marginBottom: '10px'}}>
                                <option value="">{t('adminDeviceTypes.selectConnection')}</option>
                                {btnMqttConnections.map(c => <option key={c.id} value={c.id}>{c.name || c.broker}</option>)}
                            </select>
                            <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px'}}>{t('adminDeviceTypes.mqttTopic')}</label>
                            <PlaceholderInput
                                value={btnForm.mqtt_topic}
                                onChange={v => setBtnForm({...btnForm, mqtt_topic: v})}
                                placeholders={btnPlaceholders}
                                placeholder="devices/{device_serial}/cmd"
                                style={{marginBottom: '10px'}}
                            />
                            <JsonTextarea
                                label={t('adminDeviceTypes.mqttPayload')}
                                value={btnForm.mqtt_payload}
                                onChange={v => setBtnForm({...btnForm, mqtt_payload: v})}
                                placeholders={btnPlaceholders}
                                placeholder={'{"cmd":"on","value":"{slider_value}"}'}
                            />
                        </>
                    )}
                    {parseInt(btnForm.action_type) === 6 && (
                        <>
                            <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px'}}>{t('adminDeviceTypes.apiConnection')}</label>
                            <select value={btnForm.api_connection} onChange={e => setBtnForm({...btnForm, api_connection: e.target.value})} style={{width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box', marginBottom: '10px'}}>
                                <option value="">{t('adminDeviceTypes.selectConnection')}</option>
                                {btnApiConnections.map(c => <option key={c.id} value={c.id}>{c.name || c.base_url}</option>)}
                            </select>
                            <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px'}}>{t('adminDeviceTypes.apiMethod')}</label>
                            <select value={btnForm.api_method} onChange={e => setBtnForm({...btnForm, api_method: e.target.value})} style={{width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box', marginBottom: '10px'}}>
                                {['GET','POST','PUT','PATCH','DELETE'].map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px'}}>{t('adminDeviceTypes.apiEndpoint')}</label>
                            <PlaceholderInput
                                value={btnForm.api_endpoint}
                                onChange={v => setBtnForm({...btnForm, api_endpoint: v})}
                                placeholders={btnPlaceholders}
                                placeholder="/webhook/{device_serial}"
                                style={{marginBottom: '10px'}}
                            />
                            <JsonTextarea
                                label={t('adminDeviceTypes.apiBody')}
                                value={btnForm.api_body}
                                onChange={v => setBtnForm({...btnForm, api_body: v})}
                                placeholders={btnPlaceholders}
                                placeholder={'{"value":"{slider_value}"}'}
                            />
                        </>
                    )}
                    </>);
                })()}

                <div style={{borderTop: '1px solid #F1F5F9', marginTop: '8px', paddingTop: '12px'}}>
                    <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', marginBottom: '4px', display: 'block'}}>{t('variables.booleanVarFormat')}</label>
                    <div style={{display: 'flex', gap: '8px', marginBottom: '12px'}}>
                        {[{v: 'raw', l: '0 / 1'}, {v: 'bool', l: 'True / False'}, {v: 'lower', l: 'true / false'}].map(opt => (
                            <label key={opt.v} style={{
                                display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                                padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500',
                                border: `1px solid ${btnForm.boolean_format === opt.v ? '#C7D2FE' : '#E2E8F0'}`,
                                backgroundColor: btnForm.boolean_format === opt.v ? '#EEF2FF' : '#F8FAFC',
                                color: btnForm.boolean_format === opt.v ? '#4F46E5' : '#64748B',
                            }}>
                                <input
                                    type="radio" value={opt.v}
                                    checked={btnForm.boolean_format === opt.v}
                                    onChange={() => setBtnForm({...btnForm, boolean_format: opt.v})}
                                    style={{accentColor: '#4F46E5'}}
                                />
                                {opt.l}
                            </label>
                        ))}
                    </div>
                </div>

                <div style={{borderTop: '1px solid #F1F5F9', marginTop: '8px', paddingTop: '12px'}}>
                    <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '6px'}}>{t('adminDeviceTypes.linkedVariable')}</label>
                    <select value={btnForm.linked_variable} onChange={e => setBtnForm({...btnForm, linked_variable: e.target.value})} style={{width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box', marginBottom: '10px'}}>
                        <option value="">{t('adminDeviceTypes.noLinkedVariable')}</option>
                        {variables
                            .filter(v => {
                                if (btnForm.button_type === 'slider') return v.type === 0;
                                if (btnForm.button_type === 'text') return v.type === 3;
                                return v.type === 5; // button / toggle → bool
                            })
                            .map(v => <option key={v.id} value={v.id}>{v.name}</option>)
                        }
                    </select>
                    <label style={{fontSize: '12px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px'}}>{t('adminDeviceTypes.timeoutSeconds')}</label>
                    <input type="number" min="1" value={btnForm.timeout_seconds} onChange={e => setBtnForm({...btnForm, timeout_seconds: e.target.value})} style={{width: '120px', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box'}} placeholder="15"/>
                </div>
            </Modal>
        </div>
    );
}
